import { Tabs } from "@/deps/ui-library";

export interface Segment<T extends string> {
  value: T;
  label: string;
  /** Shown after the label, e.g. a count. */
  badge?: string | number;
}

/**
 * A row of mutually exclusive choices — the status groups above a list, the tabs inside a document.
 *
 * RDS `Tabs` (pills), not RDS `SegmentGroup`: a segment group is a radio group, and what this looks
 * like to anyone using a keyboard is a tablist — a group where Tab moves *past* it and the arrow
 * keys move within it, selecting as they go. Ark gives exactly that (roving tabindex, arrow keys,
 * automatic activation). There is no tab panel: the choice filters what is below it, so the
 * triggers carry no `aria-controls` pointing at a panel that does not exist.
 */
export function SegmentControl<T extends string>({
  segments,
  value,
  onChange,
  label,
}: {
  segments: Array<Segment<T>>;
  value: T;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <Tabs.Root
      value={value}
      onValueChange={(details) => onChange(details.value as T)}
      activationMode="automatic"
      className="w-auto"
    >
      <Tabs.List aria-label={label} variant="pills" size="sm">
        {segments.map((segment) => (
          <Tabs.Trigger
            key={segment.value}
            value={segment.value}
            variant="pills"
            size="sm"
            aria-controls={undefined}
            className="text-xs"
          >
            {segment.label}
            {segment.badge !== undefined ? (
              <span className="num text-muted-foreground ml-1.5">{segment.badge}</span>
            ) : null}
          </Tabs.Trigger>
        ))}
        <Tabs.Indicator variant="pills" />
      </Tabs.List>
    </Tabs.Root>
  );
}
